import api from './api';

const riskService = {
  // Get all risks with filtering and pagination
  getAll: async (params = {}) => {
    const response = await api.get('/risks', { params });
    return response.data;
  },

  // Get risk by ID
  getById: async (id) => {
    const response = await api.get(`/risks/${id}`);
    return response.data;
  },

  // Create new risk
  create: async (riskData) => {
    const response = await api.post('/risks', riskData);
    return response.data;
  },

  // Update risk
  update: async (id, riskData) => {
    const response = await api.put(`/risks/${id}`, riskData);
    return response.data;
  },

  // Delete risk
  delete: async (id) => {
    const response = await api.delete(`/risks/${id}`);
    return response.data;
  },

  // Get risk statistics
  getStatistics: async (projectId = null) => {
    const params = projectId ? { project_id: projectId } : {};
    const response = await api.get('/risks/statistics', { params });
    return response.data;
  },

  // Get risk matrix data
  getMatrix: async (projectId = null) => {
    const params = projectId ? { project_id: projectId } : {};
    const response = await api.get('/risks/matrix', { params });
    return response.data;
  },

  // Helper: Calculate risk score
  calculateRiskScore: (probability, impact) => {
    const probabilityValues = { Low: 1, Medium: 2, High: 3 };
    const impactValues = { Low: 1, Medium: 2, High: 3, Critical: 4 };
    return probabilityValues[probability] * impactValues[impact];
  },

  // Helper: Get severity level from risk score
  getSeverityLevel: (riskScore) => {
    if (riskScore >= 10) return 'Critical';
    if (riskScore >= 7) return 'High';
    if (riskScore >= 4) return 'Medium';
    return 'Low';
  },

  // Helper: Get severity color
  getSeverityColor: (severity) => {
    const colors = {
      Critical: 'bg-red-100 text-red-800 border-red-300',
      High: 'bg-orange-100 text-orange-800 border-orange-300',
      Medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      Low: 'bg-green-100 text-green-800 border-green-300'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  },

  // Helper: Get severity badge color (for badges)
  getSeverityBadgeColor: (severity) => {
    const colors = {
      Critical: 'bg-red-500 text-white',
      High: 'bg-orange-500 text-white',
      Medium: 'bg-yellow-500 text-white',
      Low: 'bg-green-500 text-white'
    };
    return colors[severity] || 'bg-gray-500 text-white';
  },

  // Helper: Get risk type icon
  getRiskTypeIcon: (riskType) => {
    const icons = {
      Technical: '⚙️',
      Financial: '💰',
      Resource: '👥',
      Schedule: '📅',
      External: '🌐'
    };
    return icons[riskType] || '⚠️';
  },

  // Helper: Get risk type color
  getRiskTypeColor: (riskType) => {
    const colors = {
      Technical: 'bg-blue-100 text-blue-800',
      Financial: 'bg-green-100 text-green-800',
      Resource: 'bg-purple-100 text-purple-800',
      Schedule: 'bg-orange-100 text-orange-800',
      External: 'bg-gray-100 text-gray-800'
    };
    return colors[riskType] || 'bg-gray-100 text-gray-800';
  },

  // Helper: Get status color
  getStatusColor: (status) => {
    const colors = {
      Identified: 'bg-blue-100 text-blue-800',
      Analyzing: 'bg-yellow-100 text-yellow-800',
      Mitigating: 'bg-orange-100 text-orange-800',
      Closed: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  },

  // Helper: Get status icon
  getStatusIcon: (status) => {
    const icons = {
      Identified: '🔍',
      Analyzing: '🔬',
      Mitigating: '🛡️',
      Closed: '✅'
    };
    return icons[status] || '⚠️';
  },

  // Helper: Get probability color
  getProbabilityColor: (probability) => {
    const colors = {
      Low: 'bg-green-100 text-green-800',
      Medium: 'bg-yellow-100 text-yellow-800',
      High: 'bg-red-100 text-red-800'
    };
    return colors[probability] || 'bg-gray-100 text-gray-800';
  },

  // Helper: Get impact color
  getImpactColor: (impact) => {
    const colors = {
      Low: 'bg-green-100 text-green-800',
      Medium: 'bg-yellow-100 text-yellow-800',
      High: 'bg-orange-100 text-orange-800',
      Critical: 'bg-red-100 text-red-800'
    };
    return colors[impact] || 'bg-gray-100 text-gray-800';
  },

  // Helper: Get matrix cell color (for risk matrix visualization)
  getMatrixCellColor: (probability, impact) => {
    const score = riskService.calculateRiskScore(probability, impact);
    const severity = riskService.getSeverityLevel(score);
    
    const colors = {
      Critical: 'bg-red-500',
      High: 'bg-orange-500',
      Medium: 'bg-yellow-500',
      Low: 'bg-green-500'
    };
    return colors[severity] || 'bg-gray-500';
  },

  // Helper: Get matrix cell text color
  getMatrixCellTextColor: (probability, impact) => {
    const score = riskService.calculateRiskScore(probability, impact);
    return score >= 4 ? 'text-white' : 'text-gray-800';
  },

  // Helper: Format risk matrix data for visualization
  formatMatrixData: (matrixData) => {
    const probabilities = ['High', 'Medium', 'Low'];
    const impacts = ['Low', 'Medium', 'High', 'Critical'];
    
    return probabilities.map(probability => ({
      probability,
      cells: impacts.map(impact => ({
        impact,
        count: matrixData.counts?.[probability]?.[impact] || 0,
        risks: matrixData.matrix?.[probability]?.[impact] || [],
        score: riskService.calculateRiskScore(probability, impact),
        severity: riskService.getSeverityLevel(
          riskService.calculateRiskScore(probability, impact)
        ),
        color: riskService.getMatrixCellColor(probability, impact),
        textColor: riskService.getMatrixCellTextColor(probability, impact)
      }))
    }));
  },

  // Helper: Get risk types list
  getRiskTypes: () => [
    { value: 'Technical', label: 'Technical', icon: '⚙️' },
    { value: 'Financial', label: 'Financial', icon: '💰' },
    { value: 'Resource', label: 'Resource', icon: '👥' },
    { value: 'Schedule', label: 'Schedule', icon: '📅' },
    { value: 'External', label: 'External', icon: '🌐' }
  ],

  // Helper: Get probability levels
  getProbabilityLevels: () => [
    { value: 'Low', label: 'Low (1)', numeric: 1 },
    { value: 'Medium', label: 'Medium (2)', numeric: 2 },
    { value: 'High', label: 'High (3)', numeric: 3 }
  ],

  // Helper: Get impact levels
  getImpactLevels: () => [
    { value: 'Low', label: 'Low (1)', numeric: 1 },
    { value: 'Medium', label: 'Medium (2)', numeric: 2 },
    { value: 'High', label: 'High (3)', numeric: 3 },
    { value: 'Critical', label: 'Critical (4)', numeric: 4 }
  ],

  // Helper: Get status options
  getStatusOptions: () => [
    { value: 'Identified', label: 'Identified', icon: '🔍' },
    { value: 'Analyzing', label: 'Analyzing', icon: '🔬' },
    { value: 'Mitigating', label: 'Mitigating', icon: '🛡️' },
    { value: 'Closed', label: 'Closed', icon: '✅' }
  ],

  // Helper: Filter risks by severity
  filterBySeverity: async (severity, otherFilters = {}) => {
    return riskService.getAll({ severity, ...otherFilters });
  },

  // Helper: Filter risks by project
  filterByProject: async (projectId, otherFilters = {}) => {
    return riskService.getAll({ project_id: projectId, ...otherFilters });
  },

  // Helper: Filter risks by status
  filterByStatus: async (status, otherFilters = {}) => {
    return riskService.getAll({ status, ...otherFilters });
  },

  // Helper: Get active risks (not closed)
  getActiveRisks: async (projectId = null) => {
    const params = { status: 'Identified,Analyzing,Mitigating' };
    if (projectId) params.project_id = projectId;
    return riskService.getAll(params);
  },

  // Helper: Get high priority risks (High and Critical severity)
  getHighPriorityRisks: async (projectId = null) => {
    const params = { severity: 'High,Critical' };
    if (projectId) params.project_id = projectId;
    return riskService.getAll(params);
  },

  // Helper: Validate risk data
  validateRiskData: (riskData) => {
    const errors = {};

    if (!riskData.project_id) {
      errors.project_id = 'Project is required';
    }
    if (!riskData.title || riskData.title.trim() === '') {
      errors.title = 'Title is required';
    }
    if (!riskData.risk_type) {
      errors.risk_type = 'Risk type is required';
    }
    if (!riskData.probability) {
      errors.probability = 'Probability is required';
    }
    if (!riskData.impact) {
      errors.impact = 'Impact is required';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Helper: Format date for display
  formatDate: (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  // Helper: Calculate days since identified
  getDaysSinceIdentified: (identifiedDate) => {
    if (!identifiedDate) return null;
    const identified = new Date(identifiedDate);
    const now = new Date();
    const diffTime = Math.abs(now - identified);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  },

  // Helper: Check if risk is overdue for review
  isOverdueForReview: (reviewDate) => {
    if (!reviewDate) return false;
    const review = new Date(reviewDate);
    const now = new Date();
    return now > review;
  },

  // Helper: Get risk summary statistics
  getRiskSummary: (risks) => {
    const summary = {
      total: risks.length,
      bySeverity: { Low: 0, Medium: 0, High: 0, Critical: 0 },
      byStatus: { Identified: 0, Analyzing: 0, Mitigating: 0, Closed: 0 },
      byType: { Technical: 0, Financial: 0, Resource: 0, Schedule: 0, External: 0 },
      averageScore: 0
    };

    let totalScore = 0;

    risks.forEach(risk => {
      // By severity
      const severity = risk.severity || riskService.getSeverityLevel(risk.risk_score);
      if (summary.bySeverity[severity] !== undefined) {
        summary.bySeverity[severity]++;
      }

      // By status
      if (summary.byStatus[risk.status] !== undefined) {
        summary.byStatus[risk.status]++;
      }

      // By type
      if (summary.byType[risk.risk_type] !== undefined) {
        summary.byType[risk.risk_type]++;
      }

      totalScore += risk.risk_score || 0;
    });

    summary.averageScore = risks.length > 0 ? (totalScore / risks.length).toFixed(2) : 0;

    return summary;
  },

  // Helper: Sort risks by severity (descending)
  sortBySeverity: (risks) => {
    const severityOrder = { Critical: 4, High: 3, Medium: 2, Low: 1 };
    return [...risks].sort((a, b) => {
      const severityA = a.severity || riskService.getSeverityLevel(a.risk_score);
      const severityB = b.severity || riskService.getSeverityLevel(b.risk_score);
      return severityOrder[severityB] - severityOrder[severityA];
    });
  },

  // Helper: Sort risks by risk score (descending)
  sortByRiskScore: (risks) => {
    return [...risks].sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0));
  }
};

export default riskService;

